/**
 * Centralized configuration for SecureBlob webapp.
 *
 * All base paths are read from Vite environment variables (VITE_*), but
 * API base can also be overridden at runtime via window.__API_BASE__.
 */

declare global {
    interface Window {
        __API_BASE__?: string
    }
}

type OptionalImportMetaEnv = ImportMeta & {
    env?: ImportMetaEnv
}

const VITE_ENV = (import.meta as OptionalImportMetaEnv).env

/** Base path for the web application (asset prefix / router base). */
export const APP_BASE_PATH: string = VITE_ENV?.VITE_APP_BASE_PATH ?? '/'
const CRYPTOBLOBSTORE_API_SUFFIX = '/api/cryptoblobstore'
const TRUSTED_DEVICE_API_SUFFIX = '/api/identity/trusted-devices'
const RUNTIME_ERROR_API_SUFFIX = '/api/runtime-errors'

export function resolveSignupEnabled(value: string | undefined): boolean {
    const normalized = value?.trim().toLowerCase()
    return normalized === '1'
        || normalized === 'true'
        || normalized === 'yes'
        || normalized === 'on'
        || normalized === 'enabled'
}

/** Self-signup is off by default. Set VITE_SIGNUP_ENABLED=true to opt in. */
export const SIGNUP_ENABLED = resolveSignupEnabled(VITE_ENV?.VITE_SIGNUP_ENABLED)

function resolveRuntimeApiBase(): string | null {
    if (typeof window === 'undefined') return null
    const runtime = window.__API_BASE__
    if (typeof runtime !== 'string') return null
    const trimmed = runtime.trim()
    return trimmed.length > 0 ? trimmed : null
}

/**
 * Resolves the CryptoBlobStore API base URL.
 * Checked lazily so fixture-injected window.__API_BASE__ is visible at call time.
 */
export function getApiBaseUrl(): string {
    return (
        resolveRuntimeApiBase()
        ?? VITE_ENV?.VITE_API_BASE_URL
        ?? 'https://secureblob.hoglundit.se/api/cryptoblobstore'
    ).replace(/\/+$/, '')
}

/** Base URL for the CryptoBlobStore API (no trailing slash). */
export const API_BASE_URL: string = getApiBaseUrl()

/**
 * Trusted-device control plane lives alongside the CryptoBlobStore API under
 * the same host/prefix, but on `/api/identity/trusted-devices`.
 * Uses getApiBaseUrl() dynamically so fixture-injected window.__API_BASE__ is respected.
 */
export function deriveTrustedDeviceApiBase(apiBaseUrl: string): string {
    const normalized = apiBaseUrl.replace(/\/+$/, '')

    const rewritePath = (path: string): string => {
        if (path.endsWith(CRYPTOBLOBSTORE_API_SUFFIX)) {
            return `${path.slice(0, -CRYPTOBLOBSTORE_API_SUFFIX.length)}${TRUSTED_DEVICE_API_SUFFIX}`
        }

        if (path.endsWith('/cryptoblobstore')) {
            return `${path.slice(0, -'/cryptoblobstore'.length)}/identity/trusted-devices`
        }

        // Fallback: use /api/identity/trusted-devices but avoid doubling /api
        // when the path already ends with /api (e.g. 'http://localhost:5559/api').
        const base = path.endsWith('/api') ? path.slice(0, -'/api'.length) : path
        return `${base}${TRUSTED_DEVICE_API_SUFFIX}`
    }

    try {
        const url = new URL(normalized)
        url.pathname = rewritePath(url.pathname.replace(/\/+$/, ''))
        return url.toString().replace(/\/+$/, '')
    } catch {
        return rewritePath(normalized).replace(/\/+$/, '')
    }
}

/**
 * Base URL for the identity trusted-device API (no trailing slash).
 * Reads window.__API_BASE__ dynamically so fixture overrides take effect at call time.
 */
export function getTrustedDeviceApiBaseUrl(): string {
    return deriveTrustedDeviceApiBase(getApiBaseUrl())
}

/** @deprecated Use getTrustedDeviceApiBaseUrl() for lazy evaluation. */
export const TRUSTED_DEVICE_API_BASE_URL: string = getTrustedDeviceApiBaseUrl()

/**
 * Runtime-error telemetry ingress lives alongside the main SecureBlob host under
 * `/api/runtime-errors`.
 */
export function deriveRuntimeErrorApiUrl(apiBaseUrl: string): string {
    const normalized = apiBaseUrl.replace(/\/+$/, '')

    const rewritePath = (path: string): string => {
        if (path.endsWith(CRYPTOBLOBSTORE_API_SUFFIX)) {
            return `${path.slice(0, -CRYPTOBLOBSTORE_API_SUFFIX.length)}${RUNTIME_ERROR_API_SUFFIX}`
        }

        if (path.endsWith('/cryptoblobstore')) {
            return `${path.slice(0, -'/cryptoblobstore'.length)}/runtime-errors`
        }

        const base = path.endsWith('/api') ? path.slice(0, -'/api'.length) : path
        return `${base}${RUNTIME_ERROR_API_SUFFIX}`
    }

    try {
        const url = new URL(normalized)
        url.pathname = rewritePath(url.pathname.replace(/\/+$/, ''))
        return url.toString().replace(/\/+$/, '')
    } catch {
        return rewritePath(normalized).replace(/\/+$/, '')
    }
}

/**
 * Base URL for the runtime-error telemetry ingress (no trailing slash).
 * Reads window.__API_BASE__ dynamically so fixture overrides take effect at call time.
 */
export function getRuntimeErrorApiUrl(): string {
    return deriveRuntimeErrorApiUrl(getApiBaseUrl())
}

/**
 * §390: Whether browser runtime errors are forwarded to the host telemetry endpoint.
 *
 * Default: true in production builds, false in dev/test builds.
 * Can be overridden at runtime via window.__RUNTIME_ERROR_FORWARDING_ENABLED__.
 */
export function isRuntimeErrorForwardingEnabled(): boolean {
    if (typeof window === 'undefined') return true
    const runtime = (window as unknown as { __RUNTIME_ERROR_FORWARDING_ENABLED__?: boolean }).__RUNTIME_ERROR_FORWARDING_ENABLED__
    if (typeof runtime === 'boolean') return runtime
    // In production (Vite PROD mode) default to true; in dev/test default to false.
    return VITE_ENV?.PROD === true
}
